<template>
    <div class="tw:w-full" :class="props.customClass" :data-cy="props.dataCy">
        <ValidationProvider
            :name="props.name"
            :rules="computedRules"
            :value="localValue"
            v-slot="{ errors }"
            mode="eager"
        >
            <div class="tw:relative tw:w-full" :data-validation-error="errors[0] ? '' : undefined">
                <textarea
                    v-model="localValue"
                    :placeholder="props.placeholder || ' '"
                    :maxlength="props.maxlength"
                    :disabled="props.disabled || props.loading"
                    :rows="props.rows"
                    class="
                        tw:block tw:p-2 tw:w-full tw:text-sm tw:rounded tw:border tw:z-8
                        tw:appearance-none tw:focus:outline-none tw:peer
                        tw:resize-y tw:min-h-[80px]
                    "
                    :class="[
                        errors[0]
                            ? 'tw:border-red-500 tw:bg-red-50/50 tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400 tw:focus:bg-white'
                            : 'tw:border-slate-300 tw:bg-white tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400',
                    ]"
                    @input="handleInput"
                    @change="handleChange"
                ></textarea>

                <span
                    v-if="props.label"
                    class="
                        tw:absolute tw:uppercase tw:duration-300 tw:z-8 tw:pointer-events-none
                        tw:tracking-wide tw:origin-left tw:bg-white tw:px-2
                        tw:start-1 tw:top-2
                        tw:-translate-y-4 tw:scale-75
                        tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:translate-y-0
                        tw:peer-placeholder-shown:font-normal
                        tw:peer-focus:-translate-y-4 tw:peer-focus:scale-75 tw:peer-focus:px-2
                    "
                    :class="[
                        errors[0]
                            ? 'tw:text-red-400 tw:peer-placeholder-shown:bg-transparent tw:peer-focus:bg-white tw:peer-focus:text-blue-800 tw:peer-focus:font-bold'
                            : 'tw:text-slate-600 tw:font-bold tw:peer-focus:text-blue-800 tw:peer-focus:font-bold'
                    ]"
                >
                    {{ props.label }}
                </span>
            </div>

            <div
                v-if="props.maxlength"
                class="tw:flex tw:justify-between tw:items-center tw:mt-0.5"
            >
                <span
                    v-if="errors[0]"
                    class="tw:block tw:text-xs tw:text-red-600 tw:font-medium"
                >
                    {{ formatErrorMessage(errors[0]) }}
                </span>
                <span
                    v-else-if="props.apiValidationError"
                    class="tw:block tw:text-xs tw:text-red-600 tw:font-medium"
                >
                    {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
                </span>
                <span v-else></span>
                <span class="tw:text-xs tw:text-slate-400">
                    {{ localValue.length }}{{ props.maxlength ? '/' + props.maxlength : '' }}
                </span>
            </div>

            <span
                v-if="!props.maxlength && errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ formatErrorMessage(errors[0]) }}
            </span>

            <span
                v-if="!props.maxlength && props.apiValidationError && !errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';

    /**
     * BaseTextarea — Textarea con label flotante, validación y contador.
     *
     * Espejo de BaseInputText pero con <textarea> para texto multi-línea.
     * El label flota desde la parte superior (no desde el centro como en input).
     *
     * @prop {String}  value               — Texto actual.
     * @prop {String}  label               — Label flotante.
     * @prop {String}  name                — Nombre del campo para VeeValidate.
     * @prop {String|Object} [rules]       — Reglas de validación VeeValidate.
     * @prop {Boolean} [disabled]          — Deshabilitar.
     * @prop {Boolean} [loading]           — Estado de carga.
     * @prop {Number}  [maxlength]         — Máximo de caracteres.
     * @prop {Number}  [rows]              — Cantidad de filas visibles (default 3).
     * @prop {String}  [apiValidationError] — Error del servidor.
     * @prop {String}  [customClass]       — Clase extra al contenedor.
     * @prop {String}  [dataCy]            — Atributo data-cy.
     * @prop {String}  [placeholder]       — Placeholder.
     *
     * @emits input  — Nuevo valor.
     * @emits change — Valor al hacer blur.
     */
    const props = defineProps({
        label: String,
        name: String,
        rules: [String, Object],
        value: [String, Number],
        disabled: Boolean,
        loading: Boolean,
        maxlength: [String, Number],
        rows: { type: [String, Number], default: 3 },
        apiValidationError: [String, Array],
        customClass: String,
        dataCy: String,
        placeholder: String,
    });

    const emit = defineEmits(['input', 'change']);

    const computedRules = computed(() => props.rules || '');

    const localValue = ref(props.value != null ? String(props.value) : '');

    const formatErrorMessage = err => {
        const msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    const handleInput = e => {
        emit('input', e.target.value);
    };

    const handleChange = e => {
        emit('change', e.target.value);
    };

    watch(() => props.value, newVal => {
        localValue.value = newVal != null ? String(newVal) : '';
    });
</script>

<script>
    export default {
        inheritAttrs: false,
    };
</script>
